import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Shield, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function AuthShell({ title, subtitle, children }) {
    return (
        <div className="min-h-screen hud-grid flex items-center justify-center p-4">
            <div className="absolute inset-0 hazard-stripes opacity-[0.04]" />
            <div className="relative w-full max-w-md">
                <div className="flex flex-col items-center mb-6">
                    <div className="flex h-14 w-14 items-center justify-center rounded-lg border border-hazard/40 hazard-stripes mb-3">
                        <Shield size={26} className="text-hazard" />
                    </div>
                    <h1 className="font-heading text-4xl font-extrabold tracking-wider text-foreground">OUTPOST NETWORK</h1>
                    <p className="label-mono text-hazard mt-1">CONNECT · BUILD · RAID · SURVIVE</p>
                </div>
                <div className="tactical-card rounded-lg p-6">
                    <h2 className="font-heading text-2xl font-bold uppercase text-foreground">{title}</h2>
                    {subtitle && <p className="text-sm text-muted-foreground mb-5 mt-1">{subtitle}</p>}
                    {children}
                </div>
            </div>
        </div>
    );
}

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setError("");
        try {
            const u = await login(email, password);
            const admin = ["network_owner", "network_admin", "server_admin", "admin"].includes(u.role);
            navigate(admin ? "/admin/overview" : "/player/dashboard");
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally { setBusy(false); }
    };

    return (
        <AuthShell title="Operative Login" subtitle="Access your command center.">
            <form onSubmit={submit} className="space-y-4">
                {error && <div data-testid="login-error" className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">{error}</div>}
                <div>
                    <label className="label-mono text-muted-foreground">Email</label>
                    <Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                        className="mt-1 bg-background border-border" placeholder="you@outpost.gg" />
                </div>
                <div>
                    <label className="label-mono text-muted-foreground">Password</label>
                    <Input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                        className="mt-1 bg-background border-border" placeholder="••••••••" />
                </div>
                <Button data-testid="login-submit" type="submit" disabled={busy}
                    className="w-full font-heading uppercase tracking-wide bg-hazard text-black hover:bg-hazard/90">
                    {busy ? <Loader2 className="animate-spin" size={16} /> : "Deploy"}
                </Button>
                <div className="flex items-center justify-between text-sm">
                    <Link to="/forgot-password" className="text-muted-foreground hover:text-hazard">Forgot password?</Link>
                    <Link to="/register" data-testid="go-register" className="text-hazard hover:underline">Create account</Link>
                </div>
                <div className="rounded-md border border-border bg-background/50 p-3 text-xs text-muted-foreground">
                    <div className="label-mono text-radioactive mb-1">DEMO ACCESS</div>
                    Owner: sappers2016@gmail.com / OutpostOwner2026!<br />
                    Player: player@outpost.gg / player123
                </div>
            </form>
        </AuthShell>
    );
}
